import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { warning, alertCircleOutline } from 'ionicons/icons';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import { ReactComponent as PrintErrorIcon } from '@assets/print-error.svg';
import EventHeader from '@components/EventHeader';
import { Icon } from '@components/Icon';
import KioskLayout, { LockMode } from '@components/KioskLayout';
import { formatDateRange } from '@services/date-service';
import { getEventInfo } from '@state/event';
import { getKioskEnabled } from '@state/kiosk';
import { getLocale } from '@state/system';
import {
  canErrorBeRetried,
  getErrorCategory,
  getErrorCategoryMessage,
  PrinterErrorCodes,
} from '~capacitor-plugins/BadgePrinter';

const Footer = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const WarningIcon = styled(IonIcon)`
  margin-right: 8px;
`;

const Button = styled(IonButton)`
  margin-top: 16px;
  padding: 0px 16px;
`;

const AccordionGroup = styled(IonAccordionGroup)`
  width: 100%;
`;

const Accordion = styled(IonAccordion)<{ $canRetry: boolean }>`
  border-radius: 15px;
  ${({ $canRetry }) => !$canRetry && '--ion-color-light: rgba(255, 0, 0, 0.15)'};
`;

const Item = styled(IonItem)`
  --border-style: none;
`;

const selector = createSelector(
  getLocale,
  getEventInfo,
  getKioskEnabled,
  (
    locale,
    { name: eventName, imageUrl: eventImageUrl, startDate, endDate, location: eventLocation },
    kioskEnabled
  ) => ({
    locale,
    eventName,
    eventImageUrl,
    startDate,
    endDate,
    eventLocation,
    kioskEnabled,
  })
);

export interface Props {
  errorCode: PrinterErrorCodes;
  tries: number;
  retry: () => void;
  cancelRoute: string;
}

export const PrinterError = ({ errorCode, tries, retry, cancelRoute }: Props) => {
  const { locale, eventName, eventImageUrl, startDate, endDate, eventLocation, kioskEnabled } =
    useSelector(selector);

  const errorCategory = getErrorCategory(errorCode);
  const errorDescription = getErrorCategoryMessage(errorCategory!);
  const canRetry = !kioskEnabled || (tries === 1 && canErrorBeRetried(errorCategory!));
  const dangerProps = canRetry ? {} : { color: 'danger' };

  return (
    <KioskLayout
      lockMode={kioskEnabled ? LockMode.Lock : LockMode.None}
      footer={
        <Footer>
          {retry && canRetry && (
            <Button expand="block" onClick={retry}>
              {t`Try Again`}
            </Button>
          )}
          {canRetry && (
            <Button expand="block" fill="outline" routerLink={cancelRoute}>
              {t`Cancel`}
            </Button>
          )}
        </Footer>
      }
    >
      {canRetry ? (
        <>
          {kioskEnabled ? (
            <PrintErrorIcon />
          ) : (
            <Icon icon={alertCircleOutline} $color="--ion-color-danger" />
          )}
          <h1>{kioskEnabled && t`Oops, we weren't able to print your badge`}</h1>
          <p>{t`Please try again`}</p>
        </>
      ) : (
        <>
          <EventHeader
            infoText={t`Welcome to`}
            name={eventName}
            imageUrl={eventImageUrl}
            date={formatDateRange(startDate, endDate, locale)}
            location={eventLocation}
          />
          <br />
        </>
      )}
      <AccordionGroup>
        <Accordion $canRetry={canRetry} value="first">
          <Item slot="header" color="light">
            {!canRetry && <WarningIcon icon={warning} color="danger" />}
            <IonLabel {...dangerProps}>
              {canRetry
                ? t`View Details`
                : t`This machine has encountered a problem and is unavailable for use. Please contact an event organizer for assistance.`}
            </IonLabel>
          </Item>
          <Item slot="content" color="light">
            <IonLabel {...dangerProps}>
              {errorDescription} ({errorCode})
            </IonLabel>
          </Item>
        </Accordion>
      </AccordionGroup>
    </KioskLayout>
  );
};
